import React, { useState, useEffect } from "react";
import AdminSidebar from "./AdminSidebar";
import AdminTopBar from "./AdminTopBar";
import { useNavigate, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

function ChangeView({ center }) {
  const map = useMap();
  map.setView(center);
  return null;
}

export default function AdminEditFaskes() {
  const navigate = useNavigate();
  const { place_id } = useParams();
  const { logAdminActivity } = useActivityLogger();
  const [formData, setFormData] = useState({
    nama: "",
    nomorTelepon: "",
    placeID: "",
  });
  const [coordInput, setCoordInput] = useState("");
  const [position, setPosition] = useState([-6.2, 106.8]);
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Fetch existing faskes data
  useEffect(() => {
    const fetchFaskesData = async () => {
      try {
        setLoadingData(true);
        setError("");

        const response = await api.get(`/faskes/${place_id}`);
        const faskesData = response.data;

        // Set form data
        setFormData({
          nama: faskesData.name || faskesData.Nama || "",
          nomorTelepon:
            faskesData.formatted_phone_number || faskesData.NomorTelepon || "",
          placeID: faskesData.place_id || faskesData.PlaceID || "",
        });

        // Set coordinates
        if (faskesData.lat && faskesData.lng) {
          const lat = parseFloat(faskesData.lat || faskesData.Lat);
          const lng = parseFloat(faskesData.lng || faskesData.Lng);

          setCoordInput(`${lat}, ${lng}`);
          setPosition([lat, lng]);

          // Set address from faskes data or reverse geocode
          if (faskesData.vicinity || faskesData.Alamat) {
            setAddress(faskesData.vicinity || faskesData.Alamat);
          } else {
            // Reverse geocode to get address
            fetch(
              `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
            )
              .then((res) => res.json())
              .then((data) => {
                if (data && data.display_name) {
                  setAddress(data.display_name);
                } else {
                  setAddress("Alamat tidak ditemukan");
                }
              })
              .catch(() => setAddress("Gagal mendapatkan alamat"));
          }
        }
      } catch (err) {
        console.error("Error fetching faskes data:", err);
        if (err.response?.status === 404) {
          setError("Faskes tidak ditemukan");
        } else {
          setError("Gagal memuat data faskes");
        }
      } finally {
        setLoadingData(false);
      }
    };
    logAdminActivity(
      "view",
      "view_edit_faskes",
      "faskes",
      place_id,
      "admin",
      "success",
      "",
      "Admin membuka halaman edit faskes dengan PlaceID: " + place_id
    );
    if (place_id) {
      fetchFaskesData();
    }
  }, [place_id]);

  // Handle coordinate input changes with debounce
  useEffect(() => {
    if (loadingData) return; // Don't process while loading initial data

    const delayDebounceFn = setTimeout(() => {
      const coordPattern = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/;
      if (coordPattern.test(coordInput.trim())) {
        const [latStr, lonStr] = coordInput.split(",").map((v) => v.trim());
        const lat = parseFloat(latStr);
        const lon = parseFloat(lonStr);

        if (!isNaN(lat) && !isNaN(lon)) {
          setPosition([lat, lon]);

          // Reverse geocode to get address text
          fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`
          )
            .then((res) => res.json())
            .then((data) => {
              if (data && data.display_name) {
                setAddress(data.display_name);
              } else {
                setAddress("Alamat tidak ditemukan");
              }
            })
            .catch(() => setAddress("Gagal mendapatkan alamat"));
        }
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [coordInput, loadingData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (error) setError("");
  };

  const validateForm = () => {
    if (!formData.nama.trim()) {
      setError("Nama faskes wajib diisi");
      return false;
    }

    if (!formData.nomorTelepon.trim()) {
      setError("Nomor telepon wajib diisi");
      return false;
    }

    if (!coordInput.trim()) {
      setError("Koordinat wajib diisi");
      return false;
    }

    const coordPattern = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/;
    if (!coordPattern.test(coordInput.trim())) {
      setError("Format koordinat tidak valid. Gunakan format: lat, lng");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const [latStr, lonStr] = coordInput.split(",").map((v) => v.trim());
      const lat = parseFloat(latStr);
      const lng = parseFloat(lonStr);

      const faskesData = {
        place_id: formData.placeID || place_id,
        name: formData.nama,
        vicinity: address || "Alamat tidak tersedia",
        lat: lat,
        lng: lng,
        rating: 0, // Default rating
        business_status: "OPERATIONAL", // Default status
        formatted_phone_number: formData.nomorTelepon,
        maps_url: `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
      };

      const response = await api.put(`/faskes/${place_id}`, faskesData);

      if (response.status === 200) {
        setSuccess("Faskes berhasil diperbarui!");

        // Redirect after success
        logAdminActivity(
          "update",
          "update_faskes",
          "faskes",
          place_id,
          "admin",
          "success",
          "",
          "Admin berhasil memperbarui faskes dengan PlaceID: " + place_id
        );
        setTimeout(() => {
          navigate("/admin/faskes");
        }, 2000);
      }
    } catch (err) {
      console.error("Error updating faskes:", err);

      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.status === 400) {
        setError("Data yang dikirim tidak valid. Periksa kembali form Anda.");
      } else if (err.response?.status === 401) {
        setError("Anda tidak memiliki akses untuk mengedit faskes.");
      } else if (err.response?.status === 403) {
        setError("Token akses Anda tidak valid atau sudah kadaluarsa.");
      } else if (err.response?.status === 404) {
        setError("Faskes tidak ditemukan.");
      } else if (err.response?.status === 500) {
        setError("Terjadi kesalahan server. Silakan coba lagi.");
      } else {
        setError("Gagal memperbarui faskes. Periksa koneksi internet Anda.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while fetching data
  if (loadingData) {
    return (
      <div className="min-h-screen flex">
        <AdminSidebar />
        <div className="flex-1 p-6 bg-gray-50">
          <AdminTopBar />
          <div className="bg-white p-6 rounded shadow">
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
              <p className="text-gray-600">Memuat data faskes...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <AdminSidebar />
      <div className="flex-1 p-6 bg-gray-50">
        <AdminTopBar />

        <div className="bg-white p-6 rounded shadow">
          <button
            onClick={() => navigate(-1)}
            className="text-xl text-black font-semibold mb-8 hover:text-gray-600"
            disabled={loading}
          >
            &lt; Edit Faskes
          </button>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm block mb-1">
                Nama Faskes <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="nama"
                value={formData.nama}
                onChange={handleInputChange}
                placeholder="Nama faskes"
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>

            <div>
              <label className="text-sm block mb-1">
                No. HP <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="nomorTelepon"
                value={formData.nomorTelepon}
                onChange={handleInputChange}
                placeholder="No. HP"
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>

            <div>
              <label className="text-sm block mb-1">PlaceID</label>
              <input
                type="text"
                name="placeID"
                value={formData.placeID}
                onChange={handleInputChange}
                placeholder="PlaceID dari Google Maps"
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                PlaceID unik untuk identifikasi faskes
              </p>
            </div>

            <div>
              <label className="text-sm block mb-1">
                Koordinat (lat, lng) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="-6.28427577551552, 106.6676122431831"
                value={coordInput}
                onChange={(e) => setCoordInput(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>

            <div>
              <label className="text-sm block mb-1">
                Alamat (hasil reverse geocode)
              </label>
              <input
                type="text"
                value={address}
                disabled
                className="w-full border rounded px-3 py-2 text-sm bg-gray-100"
              />
            </div>

            <div className="h-64 w-full rounded overflow-hidden">
              <MapContainer
                center={position}
                zoom={16}
                style={{ height: "100%", width: "100%" }}
                key={`${position[0]}-${position[1]}`} // Force re-render when position changes
              >
                <ChangeView center={position} />
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap contributors"
                />
                <Marker position={position}>
                  <Popup>Lokasi Faskes</Popup>
                </Marker>
              </MapContainer>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 disabled:opacity-50"
                disabled={loading}
              >
                Batal
              </button>
              <button
                type="submit"
                className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? "Menyimpan..." : "Simpan Perubahan"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
